import React from "react";
import FrancoImage from "../../assets/Franco.jpg";
import Empty from "../../assets/Empty.png";
import { Link } from "react-router-dom";
import { Target, Lightbulb, Shield, Heart } from "lucide-react";
import SEO from "../Components/SEO";

const AboutUsPage = () => {
  const values = [
    {
      icon: Target,
      title: "Excellence",
      description:
        "We deliver high-quality solutions that exceed expectations and stand the test of time.",
    },
    {
      icon: Shield,
      title: "Safety First",
      description:
        "Safety is paramount. We follow strict protocols and industry standards for secure service.",
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description:
        "We embrace innovative approaches and cutting-edge technologies for efficient solutions.",
    },
    {
      icon: Heart,
      title: "Client Focus",
      description:
        "Our clients are central to everything we do. We listen, understand, and deliver tailored solutions.",
    },
  ];

  const teamMembers = [
    {
      name: "Franco Stander",
      role: "Project Manager",
      experience: "10+ years",
      image: FrancoImage,
      num: "01",
      specialties: [
        "Industrial & Domestic Electrical",
        "Power Systems",
        "Control Systems",
        "Instrumentation",
      ],
    },
    {
      name: "Rico Stander",
      role: "IT Support",
      experience: "1+ year",
      image: Empty,
      num: "02",
      specialties: ["Information Technology", "Backend Developer"],
    },
  ];

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .au-root { font-family: 'DM Sans', sans-serif; background: #fafaf8; }

    /* ── Hero ── */
    .au-hero { background: #111; border-bottom: 1px solid rgba(255,255,255,0.06); padding: 10rem 7vw 7rem; position: relative; overflow: hidden; }
    .au-hero-watermark { position: absolute; top: 50%; right: 5vw; transform: translateY(-50%); font-family: 'DM Serif Display', serif; font-size: clamp(6rem, 18vw, 16rem); color: rgba(255,255,255,0.025); line-height: 1; user-select: none; pointer-events: none; white-space: nowrap; }
    .au-hero-glow { position: absolute; bottom: -30%; left: -10%; width: 60%; height: 80%; background: radial-gradient(circle, rgba(231,135,48,0.06) 0%, transparent 70%); pointer-events: none; }
    .au-hero-content { position: relative; z-index: 1; max-width: 650px; }
    .au-eyebrow { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 2rem; }
    .au-eyebrow-line { width: 28px; height: 1px; background: #E78730; flex-shrink: 0; }
    .au-eyebrow-text { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: #E78730; font-weight: 500; }
    .au-hero h1 { font-family: 'DM Serif Display', serif; font-size: clamp(3rem, 7vw, 6rem); line-height: 0.95; color: #fff; margin-bottom: 2rem; }
    .au-hero h1 em { color: #E78730; font-style: italic; }
    .au-hero-desc { font-size: 0.95rem; color: rgba(255,255,255,0.38); line-height: 1.8; max-width: 48ch; font-weight: 300; margin-bottom: 3rem; }
    .au-hero-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
    .au-btn-primary { display: inline-block; background: #E78730; color: #fff; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none; padding: 0.9rem 2.25rem; font-family: 'DM Sans', sans-serif; transition: opacity 0.2s; }
    .au-btn-primary:hover { opacity: 0.82; }
    .au-btn-ghost { display: inline-block; border: 1px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.55); font-size: 0.7rem; font-weight: 400; letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none; padding: 0.9rem 2.25rem; font-family: 'DM Sans', sans-serif; transition: border-color 0.25s, color 0.25s; }
    .au-btn-ghost:hover { border-color: rgba(255,255,255,0.4); color: #fff; }

    /* ── Story ── */
    .au-story { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #e8e8e4; }
    .au-story-text { padding: 7rem 7vw; border-right: 1px solid #e8e8e4; }
    .au-story-img { background: #ede9e3; overflow: hidden; min-height: 560px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 0.75rem; color: #c0b9b0; }
    .au-story-img-placeholder span { font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase; }
    .au-story-label { font-size: 0.62rem; letter-spacing: 0.22em; text-transform: uppercase; color: #E78730; font-weight: 500; margin-bottom: 1.5rem; display: flex; align-items: center; gap: 0.5rem; }
    .au-story-label::before { content: ''; display: block; width: 14px; height: 1px; background: #E78730; flex-shrink: 0; }
    .au-story h2 { font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 3.5vw, 3rem); color: #1a1a1a; line-height: 1.05; margin-bottom: 2rem; }
    .au-story h2 em { color: #E78730; font-style: italic; }
    .au-story-body { font-size: 0.88rem; color: #666; line-height: 1.9; font-weight: 300; }
    .au-story-body p { margin-bottom: 1.25rem; }
    .au-story-body p:last-child { color: #E78730; font-weight: 400; margin-bottom: 0; }

    /* ── Values ── */
    .au-values { border-bottom: 1px solid #e8e8e4; }
    .au-values-header { padding: 5rem 7vw 4rem; border-bottom: 1px solid #e8e8e4; display: flex; align-items: flex-end; justify-content: space-between; gap: 3rem; flex-wrap: wrap; }
    .au-values-h2 { font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 3.5vw, 3rem); color: #1a1a1a; line-height: 1.05; margin: 0; }
    .au-values-h2 em { color: #E78730; font-style: italic; }
    .au-values-desc { font-size: 0.88rem; color: #888; line-height: 1.8; font-weight: 300; max-width: 44ch; }
    .au-values-grid { display: grid; grid-template-columns: repeat(4, 1fr); border-left: 1px solid #e8e8e4; }
    .au-value-card { padding: 3rem 2.5rem; border-right: 1px solid #e8e8e4; transition: background 0.25s; position: relative; overflow: hidden; }
    .au-value-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: #E78730; transform: scaleX(0); transform-origin: left; transition: transform 0.3s; }
    .au-value-card:hover { background: #f4f1ec; }
    .au-value-card:hover::before { transform: scaleX(1); }
    .au-value-icon { color: #E78730; margin-bottom: 1.75rem; }
    .au-value-title { font-family: 'DM Serif Display', serif; font-size: 1.35rem; color: #1a1a1a; margin-bottom: 0.75rem; }
    .au-value-desc { font-size: 0.82rem; color: #888; line-height: 1.75; font-weight: 300; }

    /* ── Team ── */
    .au-team-header { padding: 5rem 7vw 4rem; border-bottom: 1px solid #e8e8e4; display: flex; align-items: flex-end; justify-content: space-between; gap: 3rem; flex-wrap: wrap; }
    .au-team-h2 { font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 3.5vw, 3rem); color: #1a1a1a; line-height: 1.05; margin: 0; }
    .au-team-h2 em { color: #E78730; font-style: italic; }
    .au-team-desc { font-size: 0.88rem; color: #888; line-height: 1.8; font-weight: 300; max-width: 44ch; }
    .au-team-grid { display: grid; grid-template-columns: repeat(2, 1fr); border-left: 1px solid #e8e8e4; border-bottom: 1px solid #e8e8e4; max-width: 760px;   margin: 0 auto;   /* 🔥 THIS centers it */}
    .au-team-card { border-right: 1px solid #e8e8e4; overflow: hidden; }
    .au-team-img-wrap {
  width: 100%;
  height: 280px;              /* control card image height */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f5f5f5;        /* optional – nice background */
}
    .au-team-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;        /* 🔥 prevents zoom/cropping */
}
    .au-team-card:hover .au-team-img { transform: scale(1.03); filter: grayscale(0%); }
    .au-team-num { position: absolute; top: 1.5rem; left: 1.75rem; font-family: 'DM Serif Display', serif; font-size: 0.85rem; color: rgba(255,255,255,0.45); letter-spacing: 0.1em; }
    .au-team-info { padding: 2rem 2.5rem; border-top: 1px solid #e8e8e4; }
    .au-team-name { font-family: 'DM Serif Display', serif; font-size: 1.5rem; color: #1a1a1a; margin-bottom: 0.2rem; }
    .au-team-role { font-size: 0.65rem; letter-spacing: 0.18em; text-transform: uppercase; color: #E78730; font-weight: 500; margin-bottom: 1.25rem; }
    .au-team-exp { font-size: 0.75rem; color: #bbb; letter-spacing: 0.1em; margin-bottom: 1.25rem; display: flex; align-items: center; gap: 0.5rem; }
    .au-team-exp::before { content: ''; display: block; width: 12px; height: 1px; background: #e8e8e4; }
    .au-team-specialties { display: flex; flex-wrap: wrap; gap: 0.4rem; }
    .au-specialty-tag { font-size: 0.62rem; letter-spacing: 0.1em; text-transform: uppercase; color: #888; border: 1px solid #e8e8e4; padding: 0.25rem 0.65rem; font-weight: 400; }

    /* ── CTA ── */
    .au-cta { background: #111; display: grid; grid-template-columns: 1fr 1fr; border-top: 1px solid rgba(255,255,255,0.06); }
    .au-cta-left { padding: 7rem 7vw; border-right: 1px solid rgba(255,255,255,0.06); position: relative; overflow: hidden; }
    .au-cta-glow { position: absolute; top: -30%; left: -20%; width: 60%; height: 80%; background: radial-gradient(circle, rgba(231,135,48,0.06) 0%, transparent 70%); pointer-events: none; }
    .au-cta h2 { font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 4vw, 3.5rem); color: #fff; line-height: 1; margin-bottom: 1.5rem; }
    .au-cta h2 em { color: #E78730; font-style: italic; }
    .au-cta p { font-size: 0.93rem; color: rgba(255,255,255,0.38); line-height: 1.85; font-weight: 300; max-width: 44ch; margin-bottom: 3rem; }
    .au-cta-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
    .au-cta-right { padding: 7rem 7vw; display: flex; flex-direction: column; justify-content: center; }
    .au-cta-item { padding: 1.75rem 0; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; align-items: flex-start; gap: 1rem; }
    .au-cta-item:first-child { padding-top: 0; }
    .au-cta-item:last-child { border-bottom: none; padding-bottom: 0; }
    .au-cta-item-text { font-size: 0.88rem; color: rgba(255,255,255,0.45); font-weight: 300; line-height: 1.6; }

    /* ── Responsive ── */
    @media (max-width: 1024px) {
      .au-values-grid { grid-template-columns: repeat(2, 1fr); }
      .au-value-card:nth-child(2) { border-right: none; }
      .au-value-card:nth-child(3), .au-value-card:nth-child(4) { border-top: 1px solid #e8e8e4; }
    }
    @media (max-width: 900px) {
      .au-story { grid-template-columns: 1fr; }
      .au-story-text { border-right: none; border-bottom: 1px solid #e8e8e4; }
      .au-story-img { min-height: 320px; }
      .au-team-grid { grid-template-columns: 1fr; max-width: 380px; }
      .au-team-card { border-right: none; border-bottom: 1px solid #e8e8e4; }
      .au-cta { grid-template-columns: 1fr; }
      .au-cta-left { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); padding: 5rem 7vw; }
      .au-cta-right { padding: 4rem 7vw; }
      .au-values-header, .au-team-header { flex-direction: column; align-items: flex-start; }
    }
    @media (max-width: 640px) {
      .au-values-grid { grid-template-columns: 1fr; }
      .au-value-card { border-right: none; border-bottom: 1px solid #e8e8e4; }
      .au-team-img-wrap { height: 280px; }
    }
  `;

  return (
    <>
      <SEO
        title="About Us | Trademin Ltd"
        description="Learn about Trademin Ltd's commitment to quality electrical and mechanical services in Ashburton and Canterbury."
        canonical="https://www.trademin.co.nz/about-us"
        ogTitle="About Us | Trademin Ltd"
        ogDescription="Experienced electrical and mechanical professionals delivering reliable solutions across Canterbury."
      />
      <style>{styles}</style>

      <div className="au-root">
        {/* Hero */}
        <section className="au-hero">
          <div className="au-hero-glow" />
          <div className="au-hero-watermark">ABOUT</div>
          <div className="au-hero-content">
            <div className="au-eyebrow">
              <span className="au-eyebrow-line" />
              <span className="au-eyebrow-text">Established 2023</span>
            </div>
            <h1>
              About <em>Trademin</em>
            </h1>
            <p className="au-hero-desc">
              Providing comprehensive electrical and mechanical solutions across
              New Zealand — built on integrity, technical excellence, and a
              commitment to our clients.
            </p>
            <div className="au-hero-btns">
              <a href="#story" className="au-btn-primary">
                Our Story
              </a>
              <a href="#team" className="au-btn-ghost">
                Meet the Team
              </a>
            </div>
          </div>
        </section>

        {/* Story */}
        <div id="story" className="au-story">
          <div className="au-story-text">
            <div className="au-story-label">Our Story</div>
            <h2>
              Building a Legacy of <em>Engineering Excellence</em>
            </h2>
            <div className="au-story-body">
              <p>
                Trademin Ltd. was established in 2023 with a clear mission: to
                deliver exceptional electrical and mechanical engineering
                services that set new standards for quality and reliability in
                the Canterbury region.
              </p>
              <p>
                From our inception, we have been guided by core principles of
                integrity, technical excellence, and client-centered service
                delivery. Our team of highly qualified engineers and technicians
                brings decades of combined experience to every project, ensuring
                outcomes that consistently exceed industry benchmarks.
              </p>
              <p>
                Today, we are proud to serve a diverse portfolio of residential,
                commercial, and industrial clients. Our comprehensive approach
                combines cutting-edge technical expertise with personalized
                service, delivering solutions that are not only effective but
                sustainable for the long term.
              </p>
              <p>
                Every project represents our commitment to engineering
                excellence and client satisfaction.
              </p>
            </div>
          </div>
          <div className="au-story-img">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            >
              <rect x="3" y="3" width="18" height="18" rx="1" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <span
              style={{
                fontSize: "0.62rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Image placeholder
            </span>
          </div>
        </div>

        {/* Values */}
        <div className="au-values">
          <div className="au-values-header">
            <div>
              <div className="au-eyebrow" style={{ marginBottom: "1rem" }}>
                <span className="au-eyebrow-line" />
                <span className="au-eyebrow-text">Our Core Values</span>
              </div>
              <h2 className="au-values-h2">
                Principles That <em>Guide Our Work</em>
              </h2>
            </div>
            <p className="au-values-desc">
              Our values form the foundation of every decision we make and every
              project we undertake, ensuring consistent excellence in all
              aspects of our service delivery.
            </p>
          </div>
          <div className="au-values-grid">
            {values.map((value, i) => (
              <div key={i} className="au-value-card">
                <value.icon
                  size={20}
                  strokeWidth={1.25}
                  className="au-value-icon"
                />
                <h3 className="au-value-title">{value.title}</h3>
                <p className="au-value-desc">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div id="team">
          <div className="au-team-header">
            <div>
              <div className="au-eyebrow" style={{ marginBottom: "1rem" }}>
                <span className="au-eyebrow-line" />
                <span className="au-eyebrow-text">Our Leadership Team</span>
              </div>
              <h2 className="au-team-h2">
                Expert Professionals <em>Driving Success</em>
              </h2>
            </div>
            <p className="au-team-desc">
              Our team comprises highly qualified engineers and specialists with
              proven track records in delivering complex projects across diverse
              sectors.
            </p>
          </div>
          <div className="au-team-grid">
            {teamMembers.map((member, i) => (
              <div key={i} className="au-team-card">
                <div className="au-team-img-wrap">
                  <span className="au-team-num">{member.num}</span>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="au-team-img"
                  />
                </div>
                <div className="au-team-info">
                  <h3 className="au-team-name">{member.name}</h3>
                  <p className="au-team-role">{member.role}</p>
                  <p className="au-team-exp">
                    {member.experience} of experience
                  </p>
                  <div className="au-team-specialties">
                    {member.specialties.map((s, j) => (
                      <span key={j} className="au-specialty-tag">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="au-cta">
          <div className="au-cta-left">
            <div className="au-cta-glow" />
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="au-eyebrow" style={{ marginBottom: "1.5rem" }}>
                <span className="au-eyebrow-line" />
                <span className="au-eyebrow-text">Start Your Project</span>
              </div>
              <h2>
                Ready to <em>Get Started?</em>
              </h2>
              <p>
                Get a detailed, no-obligation quote tailored to your specific
                electrical and mechanical needs. Our expert team is ready to
                bring your vision to life.
              </p>
              <div className="au-cta-btns">
                <Link to="/request-quote" className="au-btn-primary">
                  Request Free Quote
                </Link>
                <Link
                  to="/contact-us"
                  className="au-btn-ghost"
                  style={{ background: "none" }}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
          <div className="au-cta-right">
            {[
              "Detailed cost breakdown with transparent pricing",
              "Comprehensive project timeline estimate",
              "Expert recommendations and solutions",
              "No hidden fees or unexpected costs",
            ].map((item, i) => (
              <div key={i} className="au-cta-item">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#E78730"
                  strokeWidth="2"
                  style={{ flexShrink: 0, marginTop: 2 }}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="au-cta-item-text">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUsPage;
